import invariant from 'invariant';
import { sprintf } from 'sprintf-js';

import { IconName } from 'components/Icon/IconName';
import { EmployerCityBucket } from 'modules/salaries/types/EmployerCityBucket';
import { MainChartData } from 'modules/salaries/types/MainChartData';
import { MainChartDetails } from 'modules/salaries/types/MainChartDetails';

function getYearsOfExperienceString(yearsOfExperience: number) {
  if (yearsOfExperience === 1) {
    return sprintf(getText('%(yearsOfExperience)s year of experience'), {
      yearsOfExperience,
    });
  }

  return sprintf(getText('%(yearsOfExperience)s years of experience'), {
    yearsOfExperience,
  });
}

function getLocationKeyInputText(
  city = '',
  state = '',
  cityBucket: EmployerCityBucket = 'NATIONAL',
) {
  const filterToTextMap = {
    CITY: `${city}, ${state}`,
    LOW: getText('Cities Like Yours'),
    MEDIUM: getText('Cities Like Yours'),
    HIGH: getText('Cities Like Yours'),
    NATIONAL: getText('All USA'),
  };

  return filterToTextMap[cityBucket];
}

export function getMainChartDetails(
  data: MainChartData,
  activeChart: 'estimatedRange' | 'submissions',
): MainChartDetails {
  invariant(data, 'chart data is required to get main chart details');

  const { filtersUsed, lineChart, submittedData } = data;

  return {
    estimatedRange: {
      description: getText(
        'The "Estimated Range" curve in this visualization is a machine learning algorithm trained on survey submissions to predict salaries for different titles, locations, and experience.',
      ),
      yearsOfExperience: submittedData?.yearsOfExperience
        ? getYearsOfExperienceString(submittedData.yearsOfExperience)
        : getText('Any number of years of experience'),
      mostCommonSalaryMin: lineChart?.sectionLimits?.middle68LowerBound ?? 0,
      mostCommonSalaryMax: lineChart?.sectionLimits?.middle68UpperBound ?? 0,
      jobTitle: submittedData?.jobTitleSelected,
      chartIcon: 'chart-bell-filled' as IconName,
      location:
        submittedData?.city && submittedData?.stateCode
          ? `${submittedData.city}, ${submittedData.stateCode}`
          : getText('All USA'),
    },
    submissions: {
      mostCommonSalaryMin: lineChart?.sectionLimits?.middle68LowerBound ?? 0,
      mostCommonSalaryMax: lineChart?.sectionLimits?.middle68UpperBound ?? 0,
      description: getText(
        'Submissions in this visualization represent individual salaries submitted by nonprofit professionals from 2018 to the present, adjusted for inflation.',
      ),
      yearsOfExperience: filtersUsed?.yearsOfExperience
        ? sprintf(getText('%(yearsOfExperience)s years of experience'), {
            yearsOfExperience: filtersUsed.yearsOfExperience.join('-'),
          })
        : getText('Any number of years of experience'),
      jobTitle: submittedData?.jobTitleSelected,
      chartIcon: 'graph-filled' as IconName,
      location: getLocationKeyInputText(
        submittedData?.city,
        submittedData?.stateCode,
        filtersUsed?.cityBucket,
      ),
    },
  }[activeChart];
}
